<template>
  <div class="pin-root" :class="{ 'is-wait': screen === 'wait' }">
    <div v-if="screen === 'boot'" class="boot" role="status">
      <span class="boot-spin" aria-hidden="true"></span>
      加载中…
    </div>

    <div v-else-if="screen === 'fatal'" class="fatal">
      <h1>{{ fatalTitle }}</h1>
      <p>{{ fatalMessage }}</p>
    </div>

    <div v-else-if="screen === 'wait'" class="wait-root">
      <div
        class="wait-screen"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wait-title"
        aria-busy="true"
      >
        <div class="wait-amount">
          <span class="wait-amount-label">本次支付</span>
          <span class="wait-amount-value"><span>{{ waitAmount }}</span> 元</span>
        </div>
        <div class="wait-ring-wrap">
          <div class="wait-ring" aria-hidden="true">
            <svg class="wait-rings" viewBox="0 0 140 140">
              <defs>
                <linearGradient id="pin-wait-arc" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stop-color="#22c55e" />
                  <stop offset="55%" stop-color="#1677ff" />
                  <stop offset="100%" stop-color="#1677ff" />
                </linearGradient>
              </defs>
              <circle cx="70" cy="70" r="64" fill="none" stroke="#e4ecf8" stroke-width="3" />
              <circle cx="70" cy="70" r="52" fill="none" stroke="#d6e4ff" stroke-width="7" />
              <circle
                class="wait-ring-arc"
                cx="70"
                cy="70"
                r="52"
                fill="none"
                stroke="url(#pin-wait-arc)"
                stroke-width="7"
                stroke-linecap="round"
              />
            </svg>
            <div class="wait-ring-core">
              <svg viewBox="0 0 24 24" width="40" height="40" aria-hidden="true">
                <path fill="#1677ff" d="M12 2.2l7.5 2.7v6.2c0 5.1-3.3 9.8-7.5 11-4.2-1.2-7.5-5.9-7.5-11V4.9L12 2.2z" />
                <path fill="#fff" d="M10.3 15.1L7.8 12.6l1.3-1.3 1.2 1.2 3.8-3.8 1.3 1.3-5.1 5.1z" />
              </svg>
            </div>
          </div>
        </div>
        <h2 id="wait-title" class="wait-title">正在生成支付链接…</h2>
        <p class="wait-sub">请稍候，勿关闭页面</p>
        <div class="wait-divider"></div>
        <ol class="wait-steps">
          <li v-for="(step, index) in steps" :key="step.name" :data-state="stepState(index)">
            <span class="wait-step-icon" aria-hidden="true"></span>
            <span class="wait-step-text">
              <span class="wait-step-name">{{ step.name }}</span>
              <span class="wait-step-desc">{{ step.desc }}</span>
            </span>
            <span class="wait-step-status">{{ stepStatus(index) }}</span>
          </li>
        </ol>
        <p class="wait-warn">请勿关闭页面或重复提交</p>
      </div>
    </div>

    <div v-else class="amount-sheet">
      <div class="amount-main">
        <h1 class="merchant">{{ title }}</h1>
        <form @submit.prevent="pay">
          <div class="amount-entry-row" :class="{ 'is-empty': !raw, 'is-error': hint.error }">
            <span class="yen" aria-hidden="true">¥</span>
            <span class="amount-shown" :class="{ 'is-placeholder': !raw }">{{ raw || '0.00' }}</span>
            <span class="caret" aria-hidden="true"></span>
          </div>
          <p class="amount-hint" :class="{ 'is-error': hint.error }">{{ hint.text }}</p>
          <button
            type="submit"
            class="submit-btn"
            :disabled="!canSubmit"
          >
            去支付
          </button>
        </form>
      </div>
      <div class="amount-dock">
        <div class="pad">
          <button
            v-for="key in digitKeys"
            :key="key"
            type="button"
            class="pad-key"
            @click="append(key)"
          >{{ key }}</button>
          <button type="button" class="pad-key" aria-label="小数点" @click="append('.')">.</button>
          <button type="button" class="pad-key" @click="append('0')">0</button>
          <button type="button" class="pad-key pad-del" aria-label="删除" @click="append('backspace')">删除</button>
        </div>
      </div>
    </div>

    <div v-if="dialog" class="dlg-backdrop">
      <div class="dlg-box" role="dialog" aria-modal="true" aria-labelledby="dlg-msg">
        <p id="dlg-msg" class="dlg-msg">{{ dialog }}</p>
        <div class="dlg-actions">
          <button type="button" @click="dialog = ''">我知道了</button>
        </div>
      </div>
    </div>

    <div v-if="showGo && payUrl" class="dlg-backdrop">
      <div class="dlg-box" role="dialog" aria-modal="true" aria-label="前往支付">
        <div class="dlg-actions">
          <a class="dlg-pay-link" :href="payUrl">前往支付</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { executeDouyinzsPin, fetchDouyinzsPinPage } from '../api/api'

/** 竞品收银台 /c/$token 金额页 + /$tradeNo 等待页。停留与脚本里的默认 25 秒一致。 */
const MIN_STAY_MS = 25000
const RESUME_KEEP_MS = MIN_STAY_MS + 10 * 60 * 1000
const STEPS = [
  { name: '确认订单', desc: '核对商户号与支付金额' },
  { name: '创建支付订单', desc: '连接当前可用支付线路' },
  { name: '准备支付链接', desc: '生成本次专用支付地址' },
  { name: '即将跳转支付', desc: '完成支付前页面准备' }
]

function resumeKey (token) {
  return 'douyinzspin-wait:' + token
}

function safePayUrl (url) {
  return typeof url === 'string' && /^(https?:|alipays:)/i.test(url) && url.indexOf('/douyinzspin/') < 0
}

function readResume (token) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(resumeKey(token)) || 'null')
    if (!saved || !safePayUrl(saved.payUrl) || !saved.startedAt) return null
    if (Date.now() - Number(saved.startedAt) > RESUME_KEEP_MS) return null
    return saved
  } catch (e) {
    return null
  }
}

function writeResume (token, amount, payUrl, startedAt) {
  try {
    sessionStorage.setItem(resumeKey(token), JSON.stringify({
      amount: amount,
      payUrl: payUrl,
      startedAt: startedAt
    }))
  } catch (e) { /* 隐私模式写不进时，只靠当前页停留 */ }
}

function clearResume (token) {
  try {
    sessionStorage.removeItem(resumeKey(token))
  } catch (e) { /* 存储不可用时忽略 */ }
}

export default {
  data () {
    return {
      screen: 'boot',
      title: '抖音钻石',
      fatalTitle: '置顶码无效',
      fatalMessage: '请重新扫码',
      minAmount: '',
      maxAmount: '',
      raw: '',
      dialog: '',
      payUrl: '',
      showGo: false,
      startedAt: 0,
      now: 0,
      jumped: false,
      alive: true,
      steps: STEPS,
      digitKeys: ['1', '2', '3', '4', '5', '6', '7', '8', '9']
    }
  },
  computed: {
    parsed () {
      const raw = this.raw
      if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
        return { violation: null, submit: false }
      }
      const fen = this.toFen(raw)
      const minFen = this.toFen(this.minAmount)
      const maxFen = this.toFen(this.maxAmount)
      if (fen == null || minFen == null || maxFen == null) {
        return { violation: null, submit: false }
      }
      let violation = null
      if (fen < minFen) violation = 'min'
      else if (fen > maxFen) violation = 'max'
      return { violation: violation, submit: fen > 0 && violation == null }
    },
    canSubmit () {
      return this.screen === 'amount' && this.parsed.submit && !this.dialog
    },
    hint () {
      if (this.parsed.violation === 'min') {
        return { text: '低于最低 ¥' + this.minAmount, error: true }
      }
      if (this.parsed.violation === 'max') {
        return { text: '超过最高 ¥' + this.maxAmount, error: true }
      }
      const parts = []
      if (this.minAmount) parts.push('最低 ¥' + this.minAmount)
      if (this.maxAmount) parts.push('最高 ¥' + this.maxAmount)
      return { text: parts.join(' · '), error: false }
    },
    waiting () {
      if (this.screen !== 'wait' || this.jumped) return false
      if (this.payUrl) return this.now - this.startedAt < MIN_STAY_MS
      return true
    },
    doneCount () {
      if (!this.waiting) return STEPS.length
      const elapsed = this.now - this.startedAt
      let count = 0
      for (let i = 1; i <= STEPS.length; i++) {
        if (elapsed >= MIN_STAY_MS * i / STEPS.length) count += 1
      }
      if (count >= STEPS.length && !this.payUrl) return STEPS.length - 1
      return count
    },
    waitAmount () {
      const value = Number(this.raw)
      if (!isFinite(value)) return this.raw
      if (Math.abs(value - Math.round(value)) < 1e-9) return String(Math.round(value))
      return value.toFixed(2)
    }
  },
  mounted () {
    document.title = '收银台'
    window.addEventListener('keydown', this.onKey)
    this.load()
  },
  beforeDestroy () {
    this.alive = false
    window.removeEventListener('keydown', this.onKey)
    this.stopTick()
    if (this.jumpTimer) {
      window.clearTimeout(this.jumpTimer)
      this.jumpTimer = 0
    }
  },
  methods: {
    toFen (raw) {
      const text = String(raw || '').trim()
      if (!/^\d+(\.\d{1,2})?$/.test(text)) return null
      const parts = text.split('.')
      const frac = (parts[1] || '').padEnd(2, '0')
      const fen = Number(parts[0]) * 100 + Number(frac)
      if (!isFinite(fen) || fen < 0) return null
      return fen
    },
    token () {
      return (this.$route.params && this.$route.params.token) || ''
    },
    load () {
      const token = this.token()
      if (!token) {
        this.failPage('置顶码无效', '请重新扫码')
        return
      }
      fetchDouyinzsPinPage(token).then(data => {
        if (!this.alive) return
        this.title = (data && data.title) || '抖音钻石'
        this.minAmount = (data && data.minAmount) || ''
        this.maxAmount = (data && data.maxAmount) || ''
        if (!this.minAmount || !this.maxAmount) {
          this.failPage('置顶码未设置金额范围', '请联系商户')
          return
        }
        const saved = readResume(token)
        const savedFen = saved ? this.toFen(saved.amount) : null
        const minFen = this.toFen(this.minAmount)
        const maxFen = this.toFen(this.maxAmount)
        if (saved && savedFen != null && minFen != null && maxFen != null && savedFen >= minFen && savedFen <= maxFen) {
          this.resumeWait(saved)
          return
        }
        if (saved) clearResume(token)
        document.title = this.title
        this.screen = 'amount'
      }).catch(err => {
        if (!this.alive) return
        this.failPage('置顶码无效', (err && err.message) || '请重新扫码')
      })
    },
    failPage (title, message) {
      this.fatalTitle = title
      this.fatalMessage = message
      this.screen = 'fatal'
      document.title = title
    },
    append (key) {
      if (this.screen !== 'amount' || this.dialog) return
      this.raw = this.nextRaw(this.raw, key)
    },
    nextRaw (raw, key) {
      if (key === 'backspace') return raw.slice(0, -1)
      if (key === '.') {
        if (raw.includes('.')) return raw
        return raw === '' ? '0.' : raw + '.'
      }
      if (!/^\d$/.test(key)) return raw
      if (raw.includes('.')) {
        const frac = (raw.split('.')[1] || '')
        if (frac.length >= 2) return raw
        return raw + key
      }
      if (raw === '' || raw === '0') return key
      if (raw.length >= 10) return raw
      return raw + key
    },
    onKey (event) {
      if (this.screen !== 'amount' || this.dialog) return
      if (event.key >= '0' && event.key <= '9') {
        event.preventDefault()
        this.append(event.key)
      } else if (event.key === '.' || event.key === 'Decimal') {
        event.preventDefault()
        this.append('.')
      } else if (event.key === 'Backspace') {
        event.preventDefault()
        this.append('backspace')
      } else if (event.key === 'Enter') {
        event.preventDefault()
        this.pay()
      }
    },
    pay () {
      if (this.screen === 'wait' || this.jumped) return
      if (!this.canSubmit) {
        if (!this.raw) this.dialog = '请输入金额'
        return
      }
      const amount = this.raw
      const token = this.token()
      clearResume(token)
      this.payUrl = ''
      this.showGo = false
      this.jumped = false
      this.startedAt = Date.now()
      this.now = this.startedAt
      this.screen = 'wait'
      document.title = '收银台'
      this.startTick()
      executeDouyinzsPin(token, amount).then(data => {
        if (!this.alive || this.screen !== 'wait' || this.startedAt === 0) return
        const payUrl = data && data.payUrl
        if (!safePayUrl(payUrl)) {
          this.failWait('未返回支付链接')
          return
        }
        this.payUrl = payUrl
        writeResume(token, amount, payUrl, this.startedAt)
        if (this.now - this.startedAt >= MIN_STAY_MS) this.goPay()
      }).catch(err => {
        if (!this.alive || this.screen !== 'wait') return
        this.failWait((err && err.message) || '拉起支付失败')
      })
    },
    resumeWait (saved) {
      this.raw = String(saved.amount || '')
      this.payUrl = saved.payUrl
      this.showGo = false
      this.jumped = false
      this.startedAt = Number(saved.startedAt)
      this.now = Date.now()
      this.screen = 'wait'
      document.title = '收银台'
      this.startTick()
      if (this.now - this.startedAt >= MIN_STAY_MS) this.goPay()
    },
    failWait (message) {
      this.stopTick()
      clearResume(this.token())
      this.payUrl = ''
      this.startedAt = 0
      this.screen = 'amount'
      this.dialog = message
      document.title = this.title
    },
    startTick () {
      this.stopTick()
      this.tickTimer = window.setInterval(() => {
        this.now = Date.now()
        if (this.payUrl && this.now - this.startedAt >= MIN_STAY_MS) this.goPay()
      }, 200)
    },
    stopTick () {
      if (this.tickTimer) {
        window.clearInterval(this.tickTimer)
        this.tickTimer = 0
      }
    },
    goPay () {
      if (this.jumped || !safePayUrl(this.payUrl)) return
      this.jumped = true
      this.stopTick()
      clearResume(this.token())
      const payUrl = this.payUrl
      window.location.replace(payUrl)
      this.jumpTimer = window.setTimeout(() => {
        if (this.alive) this.showGo = true
      }, 1000)
    },
    stepState (index) {
      if (index < this.doneCount) return 'done'
      if (index === this.doneCount) return 'active'
      return 'wait'
    },
    stepStatus (index) {
      const state = this.stepState(index)
      if (state === 'done') return '完成'
      if (state === 'active') return '处理中'
      return '等待'
    }
  }
}
</script>

<style scoped>
/* 含 px 的声明必须紧跟 no 注释，否则打包按 75 折成 rem。见通道开发规格 3.4.1 */
.pin-root {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  box-sizing: border-box;
  color: #111;
  background: #fff;
  font-family: "PingFang SC", "Microsoft YaHei", "Source Han Sans SC", system-ui, sans-serif;
  font-size: 16px; /*no*/
}
.pin-root.is-wait {
  background: #f5f6f8;
}
.boot,
.fatal {
  box-sizing: border-box;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px; /*no*/
  padding: 24px; /*no*/
  text-align: center;
  color: #8a8f99;
}
.fatal h1 {
  margin: 0;
  color: #111;
  font-size: 18.4px; /*no*/
}
.fatal p {
  margin: 0;
  max-width: 288px; /*no*/
  line-height: 1.6;
}
.boot-spin {
  box-sizing: border-box;
  width: 20px; /*no*/
  height: 20px; /*no*/
  border: 2px solid currentColor; /*no*/
  border-right-color: transparent;
  border-radius: 50%;
  animation: boot-spin 0.7s linear infinite;
}
@keyframes boot-spin {
  to { transform: rotate(360deg); }
}
.amount-sheet {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px; /*no*/
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0 auto;
}
.amount-main {
  flex: 1;
  padding: 24.8px 20px 17.6px; /*no*/
}
.merchant {
  margin: 0 0 27.2px; /*no*/
  text-align: center;
  word-break: break-word;
  font-size: 16.8px; /*no*/
  font-weight: 600;
  line-height: 1.35;
}
.amount-entry-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3.2px; /*no*/
  min-height: 51.2px; /*no*/
}
.amount-entry-row.is-empty .yen {
  color: #d0d3d8;
}
.yen {
  flex: none;
  font-size: 28px; /*no*/
  font-weight: 700;
  letter-spacing: -0.02em;
}
.amount-shown {
  font-size: 40.8px; /*no*/
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.03em;
}
.amount-shown.is-placeholder {
  color: #d0d3d8;
  font-weight: 600;
}
.caret {
  flex: none;
  width: 2px; /*no*/
  height: 29.6px; /*no*/
  margin-left: 1px; /*no*/
  background: #1677ff;
  animation: amount-caret 1s step-end infinite;
}
@keyframes amount-caret {
  50% { background: transparent; }
}
.amount-entry-row.is-error .yen,
.amount-entry-row.is-error .amount-shown {
  color: #e03e3e;
}
.amount-hint {
  margin: 11.2px 0 21.6px; /*no*/
  text-align: center;
  color: #8a8f99;
  font-size: 13.6px; /*no*/
}
.amount-hint.is-error {
  color: #e03e3e;
}
.submit-btn {
  width: 100%;
  padding: 13.6px; /*no*/
  border: 0;
  border-radius: 8px; /*no*/
  background: #1677ff;
  color: #fff;
  font-size: 16px; /*no*/
  cursor: pointer;
}
.submit-btn:disabled {
  background: #b7d2ff;
  color: #fff;
  cursor: not-allowed;
}
.amount-dock {
  flex: none;
  margin-top: auto;
  padding-bottom: env(safe-area-inset-bottom, 0);
  background: #f5f6f8;
}
.pad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px; /*no*/
  background: #e6e8eb;
}
.pad-key {
  height: 56.8px; /*no*/
  border: 0;
  background: #fff;
  color: #111;
  font-size: 21.6px; /*no*/
  font-weight: 500;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
.pad-key:active {
  background: #eef0f3;
}
.pad-del {
  color: #444;
  font-size: 14.72px; /*no*/
  font-weight: 600;
}
.wait-root {
  position: fixed;
  inset: 0;
  z-index: 30;
  overflow: auto;
  text-align: left;
  background: #f5f6f8;
}
.wait-screen {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px; /*no*/
  min-height: 100%;
  margin: 0 auto;
  padding: 18.4px 17.6px calc(22.4px + env(safe-area-inset-bottom, 0)); /*no*/
}
.wait-amount {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px; /*no*/
  padding: 15.2px 16px; /*no*/
  background: #fff;
  border: 1px solid #e6e8eb; /*no*/
  border-radius: 10px; /*no*/
}
.wait-amount-label {
  color: #555;
  font-size: 14.72px; /*no*/
}
.wait-amount-value {
  color: #111;
  font-size: 23.2px; /*no*/
  font-weight: 700;
  letter-spacing: -0.02em;
}
.wait-ring-wrap {
  display: flex;
  justify-content: center;
  margin: 24.8px 0 16.8px; /*no*/
}
.wait-ring {
  position: relative;
  width: 148px; /*no*/
  height: 148px; /*no*/
}
.wait-rings {
  display: block;
  width: 100%;
  height: 100%;
}
.wait-ring-arc {
  transform-origin: 50% 50%;
  transform-box: fill-box;
  stroke-dasharray: 82 246;
  animation: wait-arc-spin 1.35s linear infinite;
}
@keyframes wait-arc-spin {
  from { transform: rotate(-90deg); }
  to { transform: rotate(270deg); }
}
.wait-ring-core {
  position: absolute;
  inset: 28px; /*no*/
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 10px #1677ff1f; /*no*/
}
.wait-title {
  margin: 0;
  text-align: center;
  color: #111;
  font-size: 20px; /*no*/
  font-weight: 700;
}
.wait-sub {
  margin: 6.4px 0 0; /*no*/
  text-align: center;
  color: #8a8f99;
  font-size: 14.08px; /*no*/
}
.wait-divider {
  height: 2px; /*no*/
  margin: 18.4px 0 4.8px; /*no*/
  background: #1677ff;
  border-radius: 1px; /*no*/
}
.wait-steps {
  margin: 0;
  padding: 3.2px 0 0; /*no*/
  list-style: none;
}
.wait-steps li {
  display: flex;
  align-items: flex-start;
  gap: 11.2px; /*no*/
  padding: 12.48px 1.6px; /*no*/
}
.wait-step-icon {
  position: relative;
  flex: none;
  box-sizing: border-box;
  width: 22px; /*no*/
  height: 22px; /*no*/
  margin-top: 2px; /*no*/
  background: #fff;
  border: 2px solid #c5c9d1; /*no*/
  border-radius: 50%;
}
.wait-steps li[data-state=active] .wait-step-icon {
  border-color: #1677ff;
}
.wait-steps li[data-state=done] .wait-step-icon {
  background: #1677ff;
  border-color: #1677ff;
}
.wait-steps li[data-state=done] .wait-step-icon:after {
  content: "";
  position: absolute;
  top: 2px; /*no*/
  left: 6px; /*no*/
  width: 5px; /*no*/
  height: 9px; /*no*/
  border: 2px solid #fff; /*no*/
  border-width: 0 2px 2px 0; /*no*/
  transform: rotate(45deg);
}
.wait-step-text {
  flex: 1;
  min-width: 0;
}
.wait-step-name {
  display: block;
  color: #1a1a1a;
  font-size: 15.2px; /*no*/
  font-weight: 600;
}
.wait-step-desc {
  display: block;
  margin-top: 3.2px; /*no*/
  color: #9aa0aa;
  font-size: 12.48px; /*no*/
}
.wait-step-status {
  flex: none;
  margin-left: auto;
  padding-top: 2px; /*no*/
  color: #c5c9d1;
  font-size: 13.12px; /*no*/
}
.wait-steps li[data-state=active] .wait-step-status {
  color: #1677ff;
}
.wait-steps li[data-state=done] .wait-step-status {
  color: #8a8f99;
}
.wait-warn {
  margin: auto 0 0;
  padding-top: 20px; /*no*/
  text-align: center;
  color: #c47b2b;
  font-size: 13.12px; /*no*/
}
.dlg-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px; /*no*/
  background: #00000073;
}
.dlg-box {
  width: 100%;
  max-width: 360px; /*no*/
  padding: 20px 17.6px 16px; /*no*/
  text-align: center;
  background: #fff;
  border-radius: 12px; /*no*/
  box-shadow: 0 8px 32px #0000002e; /*no*/
}
.dlg-msg {
  margin: 0 0 17.6px; /*no*/
  color: #222;
  font-size: 15.2px; /*no*/
  line-height: 1.5;
}
.dlg-actions {
  display: flex;
  gap: 9.6px; /*no*/
}
.dlg-actions button,
.dlg-pay-link {
  box-sizing: border-box;
  flex: 1;
  width: 100%;
  padding: 13.6px; /*no*/
  border: 0;
  border-radius: 8px; /*no*/
  background: #1677ff;
  color: #fff;
  font-size: 16px; /*no*/
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
}
@media (prefers-reduced-motion: reduce) {
  .boot-spin,
  .caret,
  .wait-ring-arc {
    animation: none;
  }
}
</style>
